import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from "@emotion/styled";
import { useState, useRef } from 'react';

type FileOrPasteProps = {
  onClick?: () => void;
  onChange: (values: string[]) => void;
}

const FileOrPaste = ({ onClick, onChange }: FileOrPasteProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file => 
      file.type === 'text/csv' || 
      file.name.toLowerCase().endsWith('.csv') ||
      file.type === 'application/vnd.ms-excel'
    );

    if (csvFile) {
      readCSVFile(csvFile);
    }
  };

  const readCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const ids = parseCSVContent(content);
      onChange(ids);
    };
    reader.readAsText(file);
  };

  const parseCSVContent = (content: string): string[] => {
    // Split by commas and newlines, then filter out empty values
    const ids = content
      .split(/[,\n\r]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
    
    return ids;
  };

  const handleBrowseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (
      file.type === 'text/csv' || 
      file.name.toLowerCase().endsWith('.csv') ||
      file.type === 'application/vnd.ms-excel'
    )) {
      readCSVFile(file);
    }
    // Reset the input value so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  return (
    <Container 
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      isDragOver={isDragOver}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,application/vnd.ms-excel"
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'none' }}
      />
      <PolarisIcon icon="cloud_upload" height={36} />
      <MainLabel>
        {isDragOver ? 'Drop your CSV file here' : (
          <div>
            Drop a csv file or <TextLink onClick={handleBrowseClick}>browse</TextLink> to import
          </div>
        )} 
        <br/> 
        or <TextLink onClick={onClick}>click here</TextLink> to paste IDs
      </MainLabel>
    </Container>
  );
};

const Container = styled.div<{ isDragOver?: boolean }>`
  background-color: ${props => props.isDragOver 
    ? props.theme.palette.action.hover 
    : props.theme.palette.background.default};
  border: ${props => props.isDragOver 
    ? `2px dashed ${props.theme.palette.primary.main}` 
    : '2px dashed transparent'};
  padding: ${props => props.theme.spacing(3)};
  min-height: 539px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
`;

const MainLabel = styled.div`
  text-align: center;
`;

const TextLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    text-decoration: none;
  }
`;

export default FileOrPaste;